import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import splash from '../assets/images/splash.png';
import DynamicLogo from '../components/DynamicLogo';
import {
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

import { getActiveToken, getActiveUserRaw } from '../utils/authStorage';

const API_URL = process.env.REACT_APP_AUTH_SERVICE_URL || '';

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [focused, setFocused] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showSuccessSplash, setShowSuccessSplash] = useState(false);
  const [successSplashLeaving, setSuccessSplashLeaving] = useState(false);

  // splash states
  const [showSplash, setShowSplash] = useState(true);
  const [splashLeaving, setSplashLeaving] = useState(false);

  const cardRef = useRef(null);
  const emailRef = useRef(null);
  const successRedirectRef = useRef(null);

  const visibleDuration = 1500;
  const exitDuration = 400;
  const successVisibleMs = 1400;
  const successExitMs = 450;

  useEffect(() => {
    if (!showSplash) {
      const id = setTimeout(() => {
        cardRef.current?.classList.remove('opacity-0', 'translate-y-6');
        emailRef.current?.focus();
      }, 160);
      return () => clearTimeout(id);
    }
  }, [showSplash]);

  useEffect(() => {
    const t1 = setTimeout(() => setSplashLeaving(true), visibleDuration);
    const t2 = setTimeout(() => setShowSplash(false), visibleDuration + exitDuration);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [visibleDuration, exitDuration]);

  useEffect(() => {
    return () => {
      if (successRedirectRef.current) {
        successRedirectRef.current.forEach(clearTimeout);
      }
    };
  }, []);

  // helper: centralize role -> route mapping
  const redirectByRole = (rawRole) => {
    const role = (String(rawRole || '').trim() || '').toLowerCase();
    console.debug('redirectByRole() role:', { rawRole, normalized: role });
    if (role === 'admin') {
      navigate('/users', { replace: true });
    } else if (role === 'manager') {
      navigate('/transaction', { replace: true });
    } else if (role === 'marketing') {
      navigate('/regit', { replace: true });
    } else if (role === 'merchant') {
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/transaction', { replace: true });
    }
  };

  const showElegantSuccessSplash = (rawRole) => {
    setShowSuccessSplash(true);
    setSuccessSplashLeaving(false);
    if (successRedirectRef.current) {
      successRedirectRef.current.forEach(clearTimeout);
    }
    const leaveTimer = setTimeout(() => setSuccessSplashLeaving(true), successVisibleMs);
    const redirectTimer = setTimeout(() => {
      redirectByRole(rawRole);
    }, successVisibleMs + successExitMs);
    successRedirectRef.current = [leaveTimer, redirectTimer];
  };

  useEffect(() => {
    const token = getActiveToken();
    if (!token) return;

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const validate = async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/me`);
        // robust extraction of user
        const user = res.data?.user ?? res.data ?? {};
        const role = user?.role ?? user?.data?.role ?? '';
        console.debug('/auth/me returned user:', user);
        redirectByRole(role);
      } catch (err) {
        // token invalide/expiré -> on le supprime et on reste sur la page login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        console.warn('Token invalide ou non vérifié — reste sur la page de login', err?.response?.data ?? err.message);
      }
    };

    validate();

  }, []); // run once on mount

  // Pré-remplir l'email si déjà en mémoire (optionnel)
  useEffect(() => {
    const savedUser = getActiveUserRaw() || localStorage.getItem('user') || sessionStorage.getItem('user');
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        if (u.email) setEmail(u.email);
      } catch (e) { /* ignore */ }
    }
  }, []);

  const isValidEmail = (v) => /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i.test(String(v).trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Format d\'email invalide.');
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(`${API_URL}/auth/login`, { email: email.trim(), password });

      // robust extraction of token/user payload
      const payload = res.data || {};
      const token = payload.token || payload.data?.token || payload.accessToken || '';
      const user = payload.user || payload.data?.user || payload.data || payload;

      if (!token) throw new Error('Token non reçu');

      // Stockage token + user selon "remember"
      if (remember) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setIsLoading(false);

      console.debug('Login success, user:', user);

      const role = user?.role ?? user?.data?.role ?? '';
      showElegantSuccessSplash(role);
    } catch (err) {
      setIsLoading(false);
      setShowSuccessSplash(false);
      setSuccessSplashLeaving(false);
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
      if (serverMsg) setError(serverMsg);
      else setError('Erreur lors de la connexion. Vérifiez vos identifiants ou réessayez.');
      console.error('Login error (front):', err);
    }
  };

  const fieldClass = (name) =>
    `w-full rounded-lg border bg-white px-3.5 py-3 pl-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 ${
      focused === name
        ? 'border-blue-800 ring-2 ring-blue-800/10'
        : 'border-slate-200 hover:border-slate-300'
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-100 text-slate-900 antialiased" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes splashIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes splashShine {
          from { transform: translateX(-130%) skewX(-18deg); }
          to { transform: translateX(130%) skewX(-18deg); }
        }
        @keyframes successLogoIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes successPulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.08); }
        }
        @keyframes dashMove {
          to { stroke-dashoffset: -20; }
        }
        .pair-line {
          stroke-dasharray: 4 7;
          animation: dashMove 1.6s linear infinite;
        }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #0f172a;
          box-shadow: 0 0 0 1000px #ffffff inset;
          transition: background-color 9999s ease-out;
        }
      `}</style>

      {showSplash && (
        <div aria-hidden="true" className="fixed inset-0 z-50 flex items-center justify-center bg-white">
          <div
            className={`px-6 ${splashLeaving ? 'opacity-0' : 'opacity-100'}`}
            style={{ transition: `opacity ${exitDuration}ms ease` }}
          >
            <div
              className="relative overflow-hidden"
              style={{ animation: `splashIn 0.4s ease both` }}
            >
              <img
                src={splash}
                alt="ECRLink"
                className="h-64 w-64 object-contain sm:h-80 sm:w-80"
              />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                style={{ animation: `splashShine 1.15s ease 0.2s both` }}
              />
            </div>
          </div>
        </div>
      )}

      {showSuccessSplash && (
        <div
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-white"
          role="status"
          aria-live="polite"
          aria-label="Chargement"
        >
          <div
            className={`flex flex-col items-center px-6 ${successSplashLeaving ? 'opacity-0' : 'opacity-100'}`}
            style={{ transition: `opacity ${successExitMs}ms ease` }}
          >
            <div
              className="relative overflow-hidden"
              style={{ animation: 'successLogoIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both' }}
            >
              <img
                src={splash}
                alt="ECRLink"
                className="h-52 w-52 object-contain sm:h-64 sm:w-64"
              />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/55 to-transparent"
                style={{ animation: 'splashShine 1.2s ease 0.15s both' }}
              />
            </div>

            <div
              className="mt-8 flex items-center gap-2"
              style={{ animation: 'successLogoIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both' }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-800" style={{ animation: 'successPulse 1.1s ease-in-out infinite' }} />
              <span className="h-1.5 w-1.5 rounded-full bg-blue-800" style={{ animation: 'successPulse 1.1s ease-in-out 0.18s infinite' }} />
              <span className="h-1.5 w-1.5 rounded-full bg-blue-800" style={{ animation: 'successPulse 1.1s ease-in-out 0.36s infinite' }} />
            </div>
          </div>
        </div>
      )}

      <div className={`flex min-h-screen ${showSplash || showSuccessSplash ? 'pointer-events-none opacity-30' : 'opacity-100'} transition-opacity duration-500`}>
        <aside className="relative hidden min-h-screen overflow-hidden md:flex md:w-[54%] md:flex-col md:justify-between md:py-14 md:pl-5 md:pr-10 lg:w-[58%] lg:pl-7 lg:pr-14 xl:pl-9 xl:pr-16">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-100 via-[#eef4fb] to-white" />
          <div className="pointer-events-none absolute -left-16 top-[-12%] h-[30rem] w-[30rem] rounded-full bg-blue-200/35" />
          <div className="pointer-events-none absolute bottom-[-14%] right-[-10%] h-[24rem] w-[24rem] rounded-full bg-blue-100/60" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-blue-200 to-transparent" />

          <div className="relative">
            <p className="text-4xl font-bold leading-[0.92] tracking-tight text-slate-900 lg:text-[3.5rem] xl:text-[4rem]">
              ECR<span className="text-blue-800">Link</span>
            </p>
            <div className="mt-4 h-[2px] w-12 bg-blue-800" />
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-slate-600">
              Caisses et TPE connectés —
              <br />
              monitoring, pairage et analytics
              <br />
              depuis un seul espace.
            </p>
          </div>

          <div className="relative ml-24 w-full max-w-lg lg:ml-36 xl:ml-44">
            <CaisseTpeLink />
          </div>

          <p className="relative text-[10px] italic tracking-[0.22em] text-slate-400">
            POWERED BY DIGICOSER
          </p>
        </aside>

        <main className="relative flex min-h-screen w-full flex-col items-center justify-center bg-slate-50/60 px-4 py-10 sm:px-8 md:w-[46%] md:px-10 lg:w-[42%] lg:px-14">
          <div
            ref={cardRef}
            className="relative w-full max-w-[26rem] rounded-2xl border border-slate-200 bg-white p-7 text-center opacity-0 shadow-[0_4px_24px_rgba(15,23,42,0.08)] transition-all duration-700 translate-y-6 sm:p-8"
          >
            <div className="flex justify-center">
              <DynamicLogo className="h-12 object-contain sm:h-14" />
            </div>

            <div className="mt-6 md:hidden">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                ECR<span className="text-blue-800">Link</span>
              </h1>
              <p className="mt-1.5 text-sm text-slate-500">Caisses et TPE connectés.</p>
              <div className="mx-auto mt-3 max-w-[240px]">
                <CaisseTpeLink compact />
              </div>
            </div>

            <h2 className="mt-8 text-[1.55rem] font-semibold tracking-tight text-slate-900">Connexion</h2>
            <p className="mt-1.5 text-sm text-slate-500">Accédez à votre espace de gestion.</p>
            <div className="mx-auto mt-4 h-[2px] w-10 bg-blue-800" />

            <form onSubmit={handleSubmit} noValidate className="mt-7 space-y-5 text-left">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-slate-500">
                  Adresse email
                </label>
                <div className="relative">
                  <EnvelopeIcon className={`pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 ${email || focused === 'email' ? 'text-blue-800' : 'text-slate-300'}`} />
                  <input
                    ref={emailRef}
                    id="email"
                    type="email"
                    className={fieldClass('email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocused('email')}
                    onBlur={() => setFocused('')}
                    placeholder="vous@exemple.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-slate-500">
                  Mot de passe
                </label>
                <div className="relative">
                  <LockClosedIcon className={`pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 ${password || focused === 'password' ? 'text-blue-800' : 'text-slate-300'}`} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`${fieldClass('password')} pr-10`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocused('password')}
                    onBlur={() => setFocused('')}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700"
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div role="alert" aria-live="assertive" className="text-sm font-medium text-rose-600">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-blue-700 to-blue-900 px-6 py-3.5 text-sm font-semibold text-white transition hover:from-blue-800 hover:to-blue-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2"><SpinnerLight /> Connexion…</span>
                ) : (
                  <>
                    <span>Se connecter</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-7 text-center text-[10px] italic tracking-[0.18em] text-slate-400 md:hidden">
              POWERED BY DIGICOSER
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function CaisseTpeLink({ compact = false }) {
  return (
    <svg viewBox="0 0 420 150" className={`w-full ${compact ? 'h-16' : 'h-36 xl:h-40'}`} aria-hidden="true">
      <rect x="18" y="28" width="118" height="78" rx="8" fill="#dbeafe" stroke="#1e40af" strokeWidth="2" />
      <rect x="30" y="40" width="94" height="42" rx="4" fill="#1e40af" />
      <rect x="38" y="48" width="38" height="6" rx="2" fill="#93c5fd" />
      <rect x="38" y="60" width="58" height="5" rx="2" fill="#60a5fa" />
      <rect x="50" y="106" width="54" height="8" rx="2" fill="#cbd5e1" />
      <rect x="36" y="114" width="82" height="10" rx="3" fill="#e2e8f0" />

      <line x1="148" y1="72" x2="272" y2="72" stroke="#1e40af" strokeWidth="2.2" className="pair-line" />
      <circle cx="210" cy="72" r="6" fill="#1e40af" />
      <circle cx="210" cy="72" r="11" fill="none" stroke="#1e3a8a" strokeWidth="2" />

      <g transform="translate(288, 22)">
        <rect x="18" y="96" width="48" height="8" rx="4" fill="#cbd5e1" />
        <rect x="22" y="8" width="40" height="90" rx="10" fill="#ffffff" stroke="#1e40af" strokeWidth="2" />
        <rect x="26" y="12" width="32" height="18" rx="6" fill="#1e40af" opacity="0.12" />
        <rect x="28" y="34" width="28" height="22" rx="4" fill="#1e3a8a" />
        <rect x="32" y="40" width="14" height="4" rx="1" fill="#22c55e" />
        <rect x="32" y="48" width="20" height="3" rx="1" fill="#93c5fd" />
        <circle cx="34" cy="66" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="42" cy="66" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="50" cy="66" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="34" cy="74" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="42" cy="74" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="50" cy="74" r="3" fill="#22c55e" stroke="#16a34a" strokeWidth="0.8" />
        <circle cx="34" cy="82" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="42" cy="82" r="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.8" />
        <circle cx="50" cy="82" r="3" fill="#1e40af" />
      </g>
    </svg>
  );
}

function SpinnerLight() {
  return (
    <svg className="h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.35)" strokeWidth="4" />
      <path d="M22 12a10 10 0 00-10-10" stroke="white" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}
